import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Send, Check, AlertCircle, Calendar, Mail, Sparkles, Sprout, X } from "lucide-react";
import { useRegion, isIndia } from "../contexts/RegionContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Budget pre-qualifier - maps a slider step to a tier + currency-aware label.
// Steps are intentionally chunky so visitors don't agonise over exact figures.
const BUDGET_STEPS_INR = [
    { value: 1, tier: "Spark",       short: "< ₹50k",         range: "Under ₹50,000",                tone: "explore"   },
    { value: 2, tier: "Spark",       short: "₹50k – ₹1L",     range: "₹50,000 – ₹1,00,000",          tone: "explore"   },
    { value: 3, tier: "Forge",       short: "₹1L – ₹3L",      range: "₹1,00,000 – ₹3,00,000",        tone: "qualified" },
    { value: 4, tier: "Forge",       short: "₹3L – ₹7L",      range: "₹3,00,000 – ₹7,00,000",        tone: "qualified" },
    { value: 5, tier: "Atlas",       short: "₹7L – ₹15L",     range: "₹7,00,000 – ₹15,00,000",       tone: "priority"  },
    { value: 6, tier: "Atlas+",      short: "₹15L+",          range: "₹15,00,000 and above",         tone: "priority"  },
];
const BUDGET_STEPS_USD = [
    { value: 1, tier: "Spark",       short: "< $1k",          range: "Under $1,000",                  tone: "explore"   },
    { value: 2, tier: "Spark",       short: "$1k – $2.5k",    range: "$1,000 – $2,500",               tone: "explore"   },
    { value: 3, tier: "Forge",       short: "$2.5k – $6k",    range: "$2,500 – $6,000",               tone: "qualified" },
    { value: 4, tier: "Forge",       short: "$6k – $12k",     range: "$6,000 – $12,000",              tone: "qualified" },
    { value: 5, tier: "Atlas",       short: "$12k – $30k",    range: "$12,000 – $30,000",             tone: "priority"  },
    { value: 6, tier: "Atlas+",      short: "$30k+",          range: "$30,000 and above",             tone: "priority"  },
];
const LEARN_STEPS_INR = [
    { value: 1, tier: "Foundations", short: "₹14,999",  range: "Foundations · ₹14,999 / learner",  tone: "explore" },
    { value: 2, tier: "AI Builder",  short: "₹29,999",  range: "AI Builder · ₹29,999 / learner",   tone: "qualified" },
    { value: 3, tier: "Pro Track",   short: "₹39,999",  range: "Pro Track · ₹39,999 / learner",    tone: "priority" },
];
const LEARN_STEPS_USD = [
    { value: 1, tier: "Foundations", short: "$399",     range: "Foundations · $399 / learner",     tone: "explore" },
    { value: 2, tier: "AI Builder",  short: "$799",     range: "AI Builder · $799 / learner",      tone: "qualified" },
    { value: 3, tier: "Pro Track",   short: "$999",     range: "Pro Track · $999 / learner",       tone: "priority" },
];

const Contact = () => {
    const { region } = useRegion();
    const [form, setForm] = useState({
        name: "", email: "", company: "", audience: "hire", message: "", budget_step: 3,
    });
    const [status, setStatus] = useState({ state: "idle", msg: "" });
    const [programmeMode, setProgrammeMode] = useState(false);
    const [intent, setIntent] = useState(null); // { kind, label, audience, budget_tier, budget_range, helper }

    useEffect(() => {
        const consume = () => {
            try {
                if (window.sessionStorage.getItem("indoblitz.programme") === "1") {
                    setProgrammeMode(true);
                    setForm((f) => ({ ...f, audience: "partner" }));
                    window.sessionStorage.removeItem("indoblitz.programme");
                    return;
                }
                const raw = window.sessionStorage.getItem("indoblitz.intent");
                if (raw) {
                    const map = {
                        "web-dev-intern": {
                            kind: "web-dev-intern",
                            label: "Internship application · Web Development",
                            helper: "Tell us about your skills, any project links, and when you can start.",
                            audience: "learn",
                            budget_tier: "Internship",
                            budget_range: "Paid internship application",
                        },
                        "associate-role": {
                            kind: "associate-role",
                            label: "Associate / junior engineer interest",
                            helper: "Tell us about your experience, portfolio, and the kind of work you want to lead.",
                            audience: "hire",
                            budget_tier: "Associate Role",
                            budget_range: "Associate / junior engineer pathway",
                        },
                        "cybersecurity-interest": {
                            kind: "cybersecurity-interest",
                            label: "Cybersecurity Foundations · Register interest",
                            helper: "We'll notify you when the cohort opens. Mention your background if relevant.",
                            audience: "learn",
                            budget_tier: "Cybersecurity Interest",
                            budget_range: "Cybersecurity Foundations - register interest",
                        },
                        "artist-spotlight": {
                            kind: "artist-spotlight",
                            label: "IndoBlitz Spotlight · Artist portfolio enquiry",
                            helper: "Tell us about your work - links to your existing reels, social pages, or any portfolio. We'll respond with the best-fit tier and a quick-start plan.",
                            audience: "hire",
                            budget_tier: "Artist Spotlight",
                            budget_range: "Artist portfolio enquiry - IndoBlitz Spotlight",
                        },
                        "impact-funder": {
                            kind: "impact-funder",
                            label: "Impact partnership · CSR · Government · Multilateral",
                            helper: "Tell us about your organisation, sector focus and Schedule VII / mission alignment. The founder will respond within 48 hours with proposed scope, KPIs and reporting cadence.",
                            audience: "partner",
                            budget_tier: "Impact Funder",
                            budget_range: "CSR / Government / Multilateral / Foundation partnership",
                        },
                        "sme-pilot": {
                            kind: "sme-pilot",
                            label: "Rural SME digital-enablement pilot",
                            helper: "Tell us about your CSR / grant mandate, target districts and scale. This is a Schedule VII-aligned rural digital-enablement and field-training program with measured livelihood outcomes.",
                            audience: "partner",
                            budget_tier: "SME Pilot",
                            budget_range: "Rural SME digital-enablement pilot (CSR / grant)",
                        },
                        "strategic-partnership": {
                            kind: "strategic-partnership",
                            label: "Strategic & investor partnership (NDA)",
                            helper: "Tell us about your firm and area of interest. Serious technology and platform partnerships are discussed privately under NDA - we'll respond to arrange a confidential conversation.",
                            audience: "partner",
                            budget_tier: "Strategic / NDA",
                            budget_range: "Strategic / investor partnership - private / NDA",
                        },
                        "partner-sydney": {
                            kind: "partner-sydney",
                            label: "Sydney · Sales & BD Partnership",
                            helper: "Tell us about your network, sectors you sell into, and the kind of commercial structure you'd like to explore (retainer, commission, hybrid).",
                            audience: "partner",
                            budget_tier: "Sydney BD",
                            budget_range: "Sydney sales & BD partnership",
                        },
                        "enrol-track": {
                            kind: "enrol-track",
                            label: "Enrol in a talent track",
                            helper: "Which track interests you? Foundations, AI Builder or Pro Track?",
                            audience: "learn",
                            budget_tier: null,
                            budget_range: null,
                        },
                    };
                    const found = map[raw];
                    if (found) {
                        setIntent(found);
                        setForm((f) => ({ ...f, audience: found.audience }));
                    }
                    window.sessionStorage.removeItem("indoblitz.intent");
                }
            } catch (e) {}
        };
        consume();
        const onProg = () => consume();
        window.addEventListener("indoblitz:programme-intent", onProg);
        window.addEventListener("indoblitz:intent", onProg);
        return () => {
            window.removeEventListener("indoblitz:programme-intent", onProg);
            window.removeEventListener("indoblitz:intent", onProg);
        };
    }, []);

    const inIndia = isIndia(region);
    const budgetScale = useMemo(() => {
        if (form.audience === "learn") return inIndia ? LEARN_STEPS_INR : LEARN_STEPS_USD;
        if (form.audience === "partner") return inIndia ? BUDGET_STEPS_INR : BUDGET_STEPS_USD;
        return inIndia ? BUDGET_STEPS_INR : BUDGET_STEPS_USD;
    }, [form.audience, inIndia]);

    const currentStep = budgetScale[Math.min(form.budget_step, budgetScale.length) - 1] || budgetScale[0];

    const onChange = (e) => {
        const { name, value, type } = e.target;
        setForm((f) => ({ ...f, [name]: type === "range" ? Number(value) : value }));
    };

    const submit = async (e) => {
        e.preventDefault();
        setStatus({ state: "submitting", msg: "" });
        try {
            let budget_tier, budget_range;
            if (programmeMode) {
                budget_tier = "Programme";
                budget_range = "Rural / NGO / First-cohort programme application";
            } else if (intent && intent.budget_tier) {
                budget_tier = intent.budget_tier;
                budget_range = intent.budget_range;
            } else {
                budget_tier = currentStep.tier;
                budget_range = currentStep.range;
            }
            const payload = {
                name: form.name,
                email: form.email,
                company: form.company || null,
                audience: form.audience,
                message: form.message || null,
                region: region.code,
                budget_tier,
                budget_range,
            };
            await axios.post(`${API}/leads`, payload);
            const successMsg = programmeMode
                ? "Application received. Bobby reviews every programme application personally - expect a reply within 48 hours."
                : intent
                    ? "Got it. Bobby reviews every application personally - expect a reply within 48 hours."
                    : "Got it. We'll reply within one business day.";
            setStatus({ state: "success", msg: successMsg });
            setForm({ name: "", email: "", company: "", audience: form.audience, message: "", budget_step: 3 });
            setProgrammeMode(false);
            setIntent(null);
        } catch (err) {
            const msg = err?.response?.data?.detail || "Something broke. Please email admin@indoblitz.com.";
            setStatus({ state: "error", msg: typeof msg === "string" ? msg : "Submission failed." });
        }
    };

    const toneColor = currentStep.tone === "priority"
        ? "text-gold border-gold/40 bg-gold/10"
        : currentStep.tone === "qualified"
            ? "text-teal border-teal/30 bg-teal/10"
            : "text-ivory/70 border-white/15 bg-white/5";

    return (
        <section id="contact" className="relative py-24 lg:py-32" data-testid="contact">
            <div className="max-w-7xl mx-auto px-5 lg:px-10">
                <div className="grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="pill"><span className="accent-dot" />Start a conversation</div>
                        <h2 className="mt-5 font-display text-5xl lg:text-6xl text-ivory tracking-tight leading-[0.95]">
                            Let's build<br />
                            <span className="italic text-gold-shimmer">what matters.</span>
                        </h2>
                        <p className="mt-6 text-ivory/65 leading-relaxed max-w-md">
                            Tell us which lane you're in. We respond within one business day with a real human and a real plan - not a templated email.
                        </p>

                        <div className="mt-10 space-y-4">
                            <div className="flex items-start gap-4 text-sm">
                                <div className="w-10 h-10 rounded-lg bg-gold/10 border border-gold/15 flex items-center justify-center shrink-0">
                                    <Mail className="w-4 h-4 text-gold" />
                                </div>
                                <div>
                                    <div className="text-ivory/50 text-xs uppercase tracking-widest font-mono">Email</div>
                                    <a href="mailto:admin@indoblitz.com" className="text-ivory hover:text-gold transition-colors">admin@indoblitz.com</a>
                                </div>
                            </div>
                            <div className="flex items-start gap-4 text-sm">
                                <div className="w-10 h-10 rounded-lg bg-gold/10 border border-gold/15 flex items-center justify-center shrink-0">
                                    <Calendar className="w-4 h-4 text-gold" />
                                </div>
                                <div>
                                    <div className="text-ivory/50 text-xs uppercase tracking-widest font-mono">Schedule</div>
                                    <div className="text-ivory">9am – 5pm, your time zone</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <motion.form
                        onSubmit={submit}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="lg:col-span-7 glass rounded-2xl p-8 lg:p-10"
                        data-testid="contact-form"
                    >
                        {programmeMode && (
                            <div
                                className="mb-6 p-4 rounded-xl flex items-start gap-3 relative"
                                style={{
                                    background: "linear-gradient(135deg, rgba(45,212,191,0.08), rgba(212,168,87,0.06))",
                                    border: "1px solid rgba(45,212,191,0.3)",
                                }}
                                data-testid="programme-mode-banner"
                            >
                                <div className="w-9 h-9 rounded-lg bg-teal/15 border border-teal/30 flex items-center justify-center shrink-0">
                                    <Sprout className="w-4 h-4 text-teal" />
                                </div>
                                <div className="flex-1">
                                    <div className="font-mono text-[10px] uppercase tracking-widest text-teal mb-1">
                                        Programme application
                                    </div>
                                    <div className="text-ivory text-sm leading-snug">
                                        You're applying for the <strong>Rural · NGO · First-cohort programme</strong>.
                                        Tell us about your organisation - Bobby reviews every application personally.
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setProgrammeMode(false)}
                                    className="shrink-0 w-7 h-7 rounded-full text-ivory/40 hover:text-ivory hover:bg-white/5 flex items-center justify-center"
                                    aria-label="Exit programme application"
                                    data-testid="programme-mode-exit"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        )}

                        {intent && !programmeMode && (
                            <div
                                className="mb-6 p-4 rounded-xl flex items-start gap-3 relative"
                                style={{
                                    background: "linear-gradient(135deg, rgba(212,168,87,0.08), rgba(20,20,26,0.5))",
                                    border: "1px solid rgba(212,168,87,0.28)",
                                }}
                                data-testid="intent-banner"
                            >
                                <div className="w-9 h-9 rounded-lg bg-gold/15 border border-gold/30 flex items-center justify-center shrink-0">
                                    <Sparkles className="w-4 h-4 text-gold" />
                                </div>
                                <div className="flex-1">
                                    <div className="font-mono text-[10px] uppercase tracking-widest text-gold mb-1">
                                        {intent.label}
                                    </div>
                                    <div className="text-ivory/85 text-sm leading-snug">
                                        {intent.helper}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setIntent(null)}
                                    className="shrink-0 w-7 h-7 rounded-full text-ivory/40 hover:text-ivory hover:bg-white/5 flex items-center justify-center"
                                    aria-label="Exit application mode"
                                    data-testid="intent-exit"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        )}

                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs uppercase tracking-widest text-ivory/50 mb-2 font-mono">Name</label>
                                <input
                                    name="name" required minLength={1} maxLength={120}
                                    value={form.name} onChange={onChange}
                                    className="field" placeholder="Your full name"
                                    data-testid="contact-name"
                                />
                            </div>
                            <div>
                                <label className="block text-xs uppercase tracking-widest text-ivory/50 mb-2 font-mono">Email</label>
                                <input
                                    name="email" type="email" required
                                    value={form.email} onChange={onChange}
                                    className="field" placeholder="you@company.com"
                                    data-testid="contact-email"
                                />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="block text-xs uppercase tracking-widest text-ivory/50 mb-2 font-mono">Company / Organisation</label>
                                <input
                                    name="company" maxLength={120}
                                    value={form.company} onChange={onChange}
                                    className="field" placeholder="Optional"
                                    data-testid="contact-company"
                                />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="block text-xs uppercase tracking-widest text-ivory/50 mb-3 font-mono">I want to…</label>
                                <div className="grid grid-cols-3 gap-2">
                                    {[
                                        { v: "learn", label: "Learn" },
                                        { v: "hire", label: "Hire IndoBlitz" },
                                        { v: "partner", label: "Partner" },
                                    ].map((opt) => (
                                        <label
                                            key={opt.v}
                                            className={`flex items-center justify-center gap-2 px-3 py-3 rounded-lg border cursor-pointer text-sm transition-all ${form.audience === opt.v ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-ivory/70 hover:border-white/25"}`}
                                            data-testid={`contact-audience-${opt.v}`}
                                        >
                                            <input
                                                type="radio" name="audience" value={opt.v}
                                                checked={form.audience === opt.v} onChange={onChange}
                                                className="sr-only"
                                            />
                                            {opt.label}
                                        </label>
                                    ))}
                                </div>
                            </div>
                            {!programmeMode && !(intent && intent.budget_tier) && (
                                <div className="sm:col-span-2">
                                    <div className="flex items-center justify-between mb-3">
                                        <label className="block text-xs uppercase tracking-widest text-ivory/50 font-mono">
                                            {form.audience === "learn" ? "Track interest" : form.audience === "partner" ? "Deal size you can open" : "Project budget"}
                                        </label>
                                        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] font-mono uppercase tracking-widest ${toneColor}`} data-testid="budget-tone-badge">
                                            <Sparkles className="w-3 h-3" />
                                            {currentStep.tone === "priority" ? "Priority lead" : currentStep.tone === "qualified" ? "Qualified" : "Exploring"}
                                        </div>
                                    </div>

                                    <input
                                        type="range"
                                        name="budget_step"
                                        min={1}
                                        max={budgetScale.length}
                                        step={1}
                                        value={form.budget_step}
                                        onChange={onChange}
                                        className="w-full h-2 appearance-none rounded-full bg-white/10 outline-none cursor-pointer accent-[#D4A857]"
                                        style={{
                                            background: `linear-gradient(90deg, #D4A857 0%, #D4A857 ${((form.budget_step - 1) / (budgetScale.length - 1)) * 100}%, rgba(255,255,255,0.08) ${((form.budget_step - 1) / (budgetScale.length - 1)) * 100}%, rgba(255,255,255,0.08) 100%)`,
                                        }}
                                        data-testid="contact-budget-slider"
                                        aria-label="Budget"
                                    />

                                    <div className="mt-3 flex items-baseline justify-between gap-3">
                                        <div>
                                            <div className="font-display text-2xl text-ivory" data-testid="budget-range-label">
                                                {currentStep.range}
                                            </div>
                                            <div className="text-xs text-ivory/45 font-mono mt-1">
                                                Recommended tier: <span className="text-gold">{currentStep.tier}</span>
                                            </div>
                                        </div>
                                        <div className="flex gap-1 shrink-0">
                                            {budgetScale.map((s) => (
                                                <span
                                                    key={s.value}
                                                    className={`w-1.5 h-1.5 rounded-full ${s.value <= form.budget_step ? "bg-gold" : "bg-white/15"}`}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="sm:col-span-2">
                                <label className="block text-xs uppercase tracking-widest text-ivory/50 mb-2 font-mono">Tell us a bit more</label>
                                <textarea
                                    name="message" rows={4} maxLength={2000}
                                    value={form.message} onChange={onChange}
                                    className="field resize-none" placeholder="Project scope, timeline, region, anything we should know."
                                    data-testid="contact-message"
                                />
                            </div>
                        </div>

                        <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
                            <div className="text-xs text-ivory/45 font-mono">
                                {programmeMode && (
                                    <span className="text-teal mr-3">Programme · </span>
                                )}
                                {intent && !programmeMode && (
                                    <span className="text-gold mr-3">{intent.label.split(" · ")[0]} · </span>
                                )}
                                Region: <span className="text-gold">{region.flag} {region.name}</span>
                            </div>
                            <button
                                type="submit"
                                disabled={status.state === "submitting"}
                                className="btn-gold disabled:opacity-60 disabled:cursor-not-allowed"
                                data-testid="contact-submit"
                            >
                                {status.state === "submitting"
                                    ? "Sending…"
                                    : programmeMode
                                        ? "Submit application"
                                        : intent
                                            ? "Submit application"
                                            : "Send message"}
                                <Send className="w-4 h-4" />
                            </button>
                        </div>

                        {status.state === "success" && (
                            <div className="mt-5 flex items-start gap-3 p-4 rounded-xl bg-teal/10 border border-teal/30 text-teal text-sm" data-testid="contact-success">
                                <Check className="w-4 h-4 mt-0.5 shrink-0" />
                                <span>{status.msg}</span>
                            </div>
                        )}
                        {status.state === "error" && (
                            <div className="mt-5 flex items-start gap-3 p-4 rounded-xl bg-destructive/10 border border-destructive/30 text-red-300 text-sm" data-testid="contact-error">
                                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                                <span>{status.msg}</span>
                            </div>
                        )}
                    </motion.form>
                </div>
            </div>
        </section>
    );
};

export default Contact;
